import {FlexRow} from '../../../components/utility/flex_row.tsx';
import {Alignment} from '../../../enums/alignment.ts';
import {FlexColumn} from '../../../components/utility/flex_column.tsx';
import {Icon} from '../../../components/icon/icon.tsx';
import {__} from '../../../support/lang/translations.ts';
import {Card} from '../../../components/card/card.tsx';
import {CardList} from '../../../components/card/card_list.tsx';
import {PageTitle} from '../../../components/page/page_title.tsx';
import {Page} from '../../../components/page/page.tsx';
import {TableRow} from '../../../components/table/table_row.tsx';
import {TableCell} from '../../../components/table/table_cell.tsx';
import {Table} from '../../../components/table/table.tsx';
import {LocationType} from '../../../gql/pwa/graphql.ts';

interface OwnProps {
    location: LocationType;
}

export function General({location}: OwnProps) {
    let extraPhoneNumbers = location.phone_extra;
    const firstEmergencyNumber =
        extraPhoneNumbers && extraPhoneNumbers.length > 0
            ? extraPhoneNumbers.find((number) => number?.title === 'nood')
            : null;

    if (firstEmergencyNumber != null) {
        extraPhoneNumbers = extraPhoneNumbers?.filter(
            (number) => number?.title !== firstEmergencyNumber.title && number?.value !== firstEmergencyNumber.value,
        );
    }
    return (
        <>
            <CardList>
                {firstEmergencyNumber ? (
                    <Card gray={true} arrow={true} to={`tel:${firstEmergencyNumber.value}`}>
                        <FlexRow>
                            <FlexColumn>
                                <FlexRow>
                                    <Icon iconClassName="fa-light fa-phone" outline={true} />
                                    <FlexColumn>
                                        <strong style={{marginLeft: '1rem'}}>
                                            {__('contact.titles.call_us_emergency')} ({firstEmergencyNumber.value})
                                        </strong>
                                    </FlexColumn>
                                </FlexRow>
                            </FlexColumn>
                        </FlexRow>
                    </Card>
                ) : null}

                <Card gray={true} arrow={true} to={`tel:${location.phone_number}`}>
                    <FlexRow>
                        <FlexColumn>
                            <FlexRow>
                                <Icon iconClassName="fa-light fa-phone" outline={true} />
                                <FlexColumn>
                                    <strong style={{marginLeft: '1rem'}}>
                                        {__('contact.titles.call_us_general')} ({location.phone_number})
                                    </strong>
                                </FlexColumn>
                            </FlexRow>
                        </FlexColumn>
                    </FlexRow>
                </Card>

                <Card gray={true} arrow={true} to="/appointment">
                    <FlexRow>
                        <FlexColumn>
                            <FlexRow>
                                <Icon iconClassName="fa-light fa-calendar" outline={true} />
                                <FlexColumn>
                                    <strong style={{marginLeft: '1rem'}}>
                                        {__('contact.titles.make_appointment')}
                                    </strong>
                                </FlexColumn>
                            </FlexRow>
                        </FlexColumn>
                    </FlexRow>
                </Card>

                <Card gray={true} arrow={true} to={`mailto:${location.email}`}>
                    <FlexRow>
                        <FlexColumn>
                            <FlexRow>
                                <Icon iconClassName="fa-light fa-envelope" outline={true} />
                                <FlexColumn>
                                    <strong style={{marginLeft: '1rem'}}>
                                        {__('contact.titles.email_us')} {location.email}
                                    </strong>
                                </FlexColumn>
                            </FlexRow>
                        </FlexColumn>
                    </FlexRow>
                </Card>
            </CardList>

            <PageTitle style={{paddingBottom: 0}}>
                <h2>{__('contact.titles.general_data')}</h2>
            </PageTitle>

            <Table alignment={Alignment.Start}>
                {location.coc_number ? (
                    <TableRow columns={1}>
                        <TableCell name={__('contact.titles.coc')} value={location.coc_number} />
                    </TableRow>
                ) : null}
                {location.tax_number ? (
                    <TableRow columns={1}>
                        <TableCell name={__('contact.titles.tax')} value={location.tax_number} />
                    </TableRow>
                ) : null}
                <TableRow columns={1}>
                    <TableCell name="WWW" value={<span>{location.website}</span>} url={location.website} />
                </TableRow>
            </Table>

            <Page style={{paddingLeft: 0, paddingRight: 0}}>
                {extraPhoneNumbers && extraPhoneNumbers.length > 0 ? (
                    <>
                        <PageTitle style={{paddingBottom: 0}}>
                            <h2>{__('contact.titles.other_data.phone_extra')}</h2>
                        </PageTitle>

                        <Table alignment={Alignment.Start}>
                            {extraPhoneNumbers.map((phone) => {
                                return phone ? (
                                    <TableRow columns={1} key={'phone' + location.id + '-' + phone.title}>
                                        <TableCell
                                            name={phone.title ?? ''}
                                            value={
                                                <>
                                                    <i className="fa-light fa-phone" />
                                                    <span>{phone.value}</span>{' '}
                                                </>
                                            }
                                            url={`tel:${phone.value}`}
                                        />
                                    </TableRow>
                                ) : null;
                            })}
                        </Table>
                    </>
                ) : null}

                {location.email_extra && location.email_extra.length > 0 ? (
                    <>
                        <PageTitle style={{paddingBottom: 0}}>
                            <h2>{__('contact.titles.other_data.email_extra')}</h2>
                        </PageTitle>

                        <Table alignment={Alignment.Start}>
                            {location.email_extra.map((email) => {
                                return email ? (
                                    <TableRow columns={1} key={'email' + location.id + '-' + email.title}>
                                        <TableCell
                                            name={email.title ?? ''}
                                            value={
                                                <>
                                                    <i className="fa-light fa-envelope" />
                                                    <span>{email.value}</span>{' '}
                                                </>
                                            }
                                            url={`mailto:${email.value}`}
                                        />
                                    </TableRow>
                                ) : null;
                            })}
                        </Table>
                    </>
                ) : null}

                {location.emergency_phone_extra && location.emergency_phone_extra.length > 0 ? (
                    <>
                        <PageTitle style={{paddingBottom: 0}}>
                            <h2>{__('contact.titles.other_data.emergency_phone_extra')}</h2>
                        </PageTitle>

                        <Table alignment={Alignment.Start}>
                            {location.emergency_phone_extra.map((emergency_phone) => {
                                return emergency_phone ? (
                                    <TableRow
                                        columns={1}
                                        key={'emergency-phone' + location.id + '-' + emergency_phone.title}
                                    >
                                        <TableCell
                                            name={emergency_phone.title ?? ''}
                                            value={
                                                <>
                                                    <i className="fa-light fa-phone" />
                                                    <span>{emergency_phone.value}</span>{' '}
                                                </>
                                            }
                                            url={`tel:${emergency_phone.value}`}
                                        />
                                    </TableRow>
                                ) : null;
                            })}
                        </Table>
                    </>
                ) : null}
            </Page>
        </>
    );
}
